import React, { useEffect, useState } from 'react';

/* Local Components */
import { DeviceGrouping } from '../units/unit-detail';
import {
  EnumDKeyDeviceType,
  EnumDKeyDeviceType as EnumDeviceType,
  EnumPanelType,
  Gpio,
  Panel,
  Property,
  Unit,
  Zone,
  VirtualPowerPlant,
  ControlGroup,
} from '../../../types/generated-types';
import { LeakDetectorSensorComponent } from '../devices/residential-devices/leak-detector-sensor';
import { ThermostatComponent } from '../devices/residential-devices/thermostat/thermostat';
import { MultiSensorComponent } from '../devices/residential-devices/multi-sensor';
import { BoilerComponent } from '../devices/central-equipment/boiler';
import { ChillerComponent } from '../devices/central-equipment/chiller';
import { ControllerComponent } from '../devices/central-equipment/controller';
import { WindowSensorComponent } from '../devices/residential-devices/window-sensor';
import { DoorSensorComponent } from '../devices/residential-devices/door-sensor';
import { LoopComponent } from '../devices/central-equipment/loop';
import { useIsSmallScreen } from '../../system/SystemMediaQueriesProvider';
import { OccupancySensorComponent } from '../devices/residential-devices/occupancy-sensor';
import { PowerMonitorComponent } from '../devices/central-equipment/power-monitor';
import { ThermistorComponent } from '../devices/central-equipment/thermistor';
import { PressureSensorComponent } from '../devices/central-equipment/pressureSensor';
import { GpioPanelComponent } from '../devices/central-equipment/gpioPanel';
import { OpenWeatherComponent } from '../devices/central-equipment/openWeather';
import {
  CubicFeetPerMinute,
  Fahrenheit,
} from '../../system/models/temperatureUnits';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { InstallableDevice } from '../installer/installable-device';
import { InstallationStatusIndicator } from '../devices/shared-ui/installation-status-indicator';
import { SmartPowerOutletComponent } from '../devices/residential-devices/smart-power-outlet';
import { RelayDeviceComponent } from '../devices/residential-devices/relay-device';
import { ActuatorComponent } from '../devices/central-equipment/actuator';
import { ModbusAirHandlerComponent } from '../devices/modbus-air-handler/modbus-air-handler';
import BorderedSection from '../shared/borderedSection';
import { useAuthenticator } from '../../auth/AuthenticationContext';
import { EnergyConsumerBuildingComponent } from '../virtual-power-plant/energy-consumer/energy-consumer-building';
import { EnergyConsumerTenantComponent } from '../virtual-power-plant/energy-consumer/energy-consumer-tenant';
import { EnergyConsumerUtilityComponent } from '../virtual-power-plant/energy-consumer/energy-consumer-utility';
import { EnergySupplierSolarSystemComponent } from '../virtual-power-plant/energy-supplier/energy-supplier-solar-system';
import { EnergySupplierWindSystemComponent } from '../virtual-power-plant/energy-supplier/energy-supplier-wind-system';
import { EnergySupplierUtilityComponent } from '../virtual-power-plant/energy-supplier/energy-supplier-utility';
import { EnergyStorageBatterySystemComponent } from '../virtual-power-plant/energy-storage/energy-storage-battery-system';
import { EnergyStorageKineticSystemComponent } from '../virtual-power-plant/energy-storage/energy-storage-kinetic-system';
import { ConductorGenericSensorComponent } from '../devices/residential-devices/conductor-generic-sensor';

import './device-grouping-detail.css';
import { VirtualPowerPlantComponent } from '../virtual-power-plant/virtual-power-plant';
import { ControlGroupComponent } from '../controlGroups/control-group';
import type { GroupingDevices } from './device-grouping-types';

export type { GroupingDevices } from './device-grouping-types';

const DEFAULT_DEVICE_ICON = 'mdi:chip';

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future use
function getIconTypeForDeviceType(type: string): string {
  const iconMap: Record<string, string> = {
    Actuator: 'f7:dial',
    Boiler: 'mdi:water-boiler',
    Chiller: 'mdi:fridge-industrial',
    Loop: 'fe:loop',
    PowerMonitor: 'ic:outline-power',
    SmartPowerOutlet: 'ic:outline-power',
    Thermostat: 'mdi:thermostat',
    WindowSensor: 'mdi:window-open',
    DoorSensor: 'mdi:door',
    RelayDevice: 'mdi:relay',
    LeakDetector: 'mdi:water-alert',
    OccupancySensor: 'mdi:motion-sensor',
    IndoorSensor: 'mdi:thermometer',
    ModbusAirHandler: 'mdi:air-conditioner',
    IndoorAirHandler: 'mdi:air-conditioner',
    Gpio: 'mdi:chip',
    Controller: 'mdi:controller',
    PressureSensor: 'mdi:gauge',
    Thermistor: 'mdi:thermometer',
    OpenWeather: 'mdi:weather-partly-cloudy',
    VSDPump: 'mdi:pump',
    VsdPump: 'mdi:pump',
    Z210Pump: 'mdi:pump',
  };
  return iconMap[type] ?? DEFAULT_DEVICE_ICON;
}

interface DeviceGroupingDetailProps {
  grouping: DeviceGrouping;
  installerView?: boolean;
  property: Partial<Property>;
  unit: Partial<Unit>;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future use
function ControlGroupDetail({ controlGroup }: { controlGroup: ControlGroup }) {
  const name =
    controlGroup.name != null
      ? controlGroup.name.split('_').join(' ')
      : 'Unnamed Control Group';
  return (
    <Typography component="div" variant="body2">
      {name}
      {controlGroup.devices?.length
        ? ` (${controlGroup.devices.length} device${controlGroup.devices.length !== 1 ? 's' : ''})`
        : ''}
    </Typography>
  );
}

export function DeviceGroupingDetail(props: DeviceGroupingDetailProps) {
  const { grouping, property, unit, installerView = false } = props;

  const { user } = useAuthenticator();
  const preferredUnits = user?.preferredUnits ?? Fahrenheit;
  const preferredAirFlowUnits =
    user?.preferredAirFlowUnits ?? CubicFeetPerMinute;
  const [groupingDevices, setGroupingDevices] = useState<GroupingDevices>();
  const [isResident, setIsResident] = useState(false);
  const [open, setOpen] = useState(true);
  const handleClick = () => setOpen(!open);
  const devicesCount = grouping.devices?.length ?? 0;

  useEffect(() => {
    if (user) {
      setIsResident(user.isResident);
    }
  }, [user]);

  /**
   * iterate over the grouping collection (the devices within a zone or panel) and
   * populate the groupingDevices Object that stores these devices keyed by their type.
   */
  useEffect(() => {
    if (grouping && grouping.devices) {
      const _groupingDevices: GroupingDevices = {
        airHandler: undefined,
        thermostat: undefined,
        pairedSensor: undefined,
        smartPowerOutlet: undefined,
        relayDevices: [],
        modbusAirHandler: undefined,
        otherAirHandlers: [],
        otherThermostats: [],
        otherMultiSensors: [],
        windowSensors: [],
        doorSensors: [],
        occupancySensors: [],
        leakDetectors: [],
        thermistors: [],
        boilers: [],
        chillers: [],
        gpios: [],
        loops: [],
        pressureSensors: [],
        powerMonitors: [],
        controllers: [],
        actuators: [],
        energyControllers: [],
        energyConverters: [],
        energyInverters: [],
        batteryManagementSystems: [],
        energyMeters: [],
        energyRectifiers: [],
        energyStorageCoolingSystems: [],
        energySwitchPanels: [],
        energySwitchPanelCircuits: [],
        fireSuppressionSystems: [],
        kineticEnergyManagementSystems: [],
        solarManagementSystems: [],
        windManagementSystems: [],
        energyConsumerBuildings: [],
        energyConsumerTenants: [],
        energyConsumerUtilities: [],
        energySupplierSolarSystems: [],
        energySupplierWindSystems: [],
        energySupplierUtilities: [],
        energyStorageBatterySystems: [],
        energyStorageKineticSystems: [],
        vsdPumps: [],
        z210Pumps: [],
        openWeatherSensors: [],
        conductorGenericSensors: [],
      };

      if (installerView || grouping.__typename !== 'ControlGroup') {
        (grouping as Zone | Panel | VirtualPowerPlant).devices
          .filter((device) => installerView || device.isConfigured)
          .forEach((device) => {
            switch (device.type) {
              case EnumDeviceType.IndoorAirHandler:
                if ((grouping as Partial<Zone>).airHandlerId === device._id) {
                  _groupingDevices.airHandler = device;
                } else {
                  _groupingDevices.otherAirHandlers.push(device);
                }
                break;
              case EnumDeviceType.Thermostat:
                if ((grouping as Partial<Zone>).thermostatId === device._id) {
                  _groupingDevices.thermostat = device;
                } else {
                  _groupingDevices.otherThermostats.push(device);
                }
                break;
              case EnumDeviceType.IndoorSensor:
                if ((grouping as Partial<Zone>).pairedSensorId === device._id) {
                  _groupingDevices.pairedSensor = device;
                } else {
                  _groupingDevices.otherMultiSensors.push(device);
                }
                break;
              case EnumDeviceType.ConductorGenericSensor:
                _groupingDevices.conductorGenericSensors.push(device);
                break;
              case EnumDeviceType.SmartPowerOutlet:
                _groupingDevices.smartPowerOutlet = device;
                break;
              case EnumDeviceType.RelayDevice:
                _groupingDevices.relayDevices.push(device);
                break;
              case EnumDeviceType.ModbusAirHandler:
                if (!isResident) {
                  _groupingDevices.modbusAirHandler = device;
                }
                break;
              case EnumDeviceType.WindowSensor:
                _groupingDevices.windowSensors.push(device);
                break;
              case EnumDeviceType.DoorSensor:
                _groupingDevices.doorSensors.push(device);
                break;
              case EnumDeviceType.OccupancySensor:
                _groupingDevices.occupancySensors.push(device);
                break;
              case EnumDeviceType.LeakDetector:
                _groupingDevices.leakDetectors.push(device);
                break;
              case EnumDeviceType.Thermistor:
                _groupingDevices.thermistors.push(device);
                break;
              case EnumDeviceType.Boiler:
                _groupingDevices.boilers.push(device);
                break;
              case EnumDeviceType.Chiller:
                _groupingDevices.chillers.push(device);
                break;
              case EnumDeviceType.Gpio:
                _groupingDevices.gpios.push(device);
                break;
              case EnumDeviceType.Loop:
                _groupingDevices.loops.push(device);
                break;
              case EnumDeviceType.PressureSensor:
                _groupingDevices.pressureSensors.push(device);
                break;
              case EnumDeviceType.PowerMonitor:
                _groupingDevices.powerMonitors.push(device);
                break;
              case EnumDeviceType.Controller:
                _groupingDevices.controllers.push(device);
                break;
              case EnumDeviceType.Actuator:
                _groupingDevices.actuators.push(device);
                break;
              case EnumDeviceType.VsdPump:
                _groupingDevices.vsdPumps.push(device);
                break;
              case EnumDeviceType.Z210Pump:
                _groupingDevices.z210Pumps.push(device);
                break;
              case EnumDeviceType.OpenWeather:
                _groupingDevices.openWeatherSensors.push(device);
                break;
              case EnumDKeyDeviceType.EnergyController:
                _groupingDevices.energyControllers.push(device);
                break;
              case EnumDKeyDeviceType.EnergyConverter:
                _groupingDevices.energyConverters.push(device);
                break;
              case EnumDKeyDeviceType.EnergyInverter:
                _groupingDevices.energyInverters.push(device);
                break;
              case EnumDKeyDeviceType.BatteryManagementSystem:
                _groupingDevices.batteryManagementSystems.push(device);
                break;
              case EnumDKeyDeviceType.EnergyMeter:
                _groupingDevices.energyMeters.push(device);
                break;
              case EnumDKeyDeviceType.EnergyRectifier:
                _groupingDevices.energyRectifiers.push(device);
                break;
              case EnumDKeyDeviceType.EnergyStorageCoolingSystem:
                _groupingDevices.energyStorageCoolingSystems.push(device);
                break;
              case EnumDKeyDeviceType.EnergySwitchPanel:
                _groupingDevices.energySwitchPanels.push(device);
                break;
              case EnumDKeyDeviceType.EnergySwitchPanelCircuit:
                _groupingDevices.energySwitchPanelCircuits.push(device);
                break;
              case EnumDKeyDeviceType.FireSuppressionSystem:
                _groupingDevices.fireSuppressionSystems.push(device);
                break;
              case EnumDKeyDeviceType.KineticEnergyManagementSystem:
                _groupingDevices.kineticEnergyManagementSystems.push(device);
                break;
              case EnumDKeyDeviceType.SolarManagementSystem:
                _groupingDevices.solarManagementSystems.push(device);
                break;
              case EnumDKeyDeviceType.WindManagementSystem:
                _groupingDevices.windManagementSystems.push(device);
                break;
              default:
                console.error(
                  `Unsupported device type: ${device.type}: ${JSON.stringify(device, null, 2)}`,
                );
            }
          });
      }

      setGroupingDevices(_groupingDevices);
    }
  }, [grouping, installerView, isResident]);

  const isSmall = useIsSmallScreen();

  if (groupingDevices) {
    if (grouping.__typename === 'ControlGroup') {
      return installerView ? null : (
        <ControlGroupComponent
          grouping={grouping as ControlGroup}
          groupingDevices={groupingDevices}
          property={property}
          unit={unit}
        />
      );
    }
    if (grouping.__typename === 'Zone') {
      return installerView ? (
        <List
          subheader={
            <ListItemButton onClick={handleClick}>
              <InstallationStatusIndicator
                isDone={(grouping.devices ?? []).every(
                  (device) => device?.installation?.status === 3,
                )}
              />
              <ListItemText
                primary={
                  <Typography sx={{ p: 1 }} variant={'h3'}>
                    {grouping.name}: {devicesCount} Device
                    {devicesCount !== 1 && 's'}
                  </Typography>
                }
              />
              {open ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          }
          dense
          sx={{
            width: {
              xs: '98%',
              sm: '75%',
            },
            borderStyle: 'solid',
            borderColor: 'lightslategray',
            m: 1,
          }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            {groupingDevices.thermostat?._id ? (
              <InstallableDevice
                key={groupingDevices.thermostat._id}
                device={groupingDevices.thermostat}
              />
            ) : null}
            {groupingDevices.pairedSensor?._id ? (
              <InstallableDevice
                key={groupingDevices.pairedSensor._id}
                device={groupingDevices.pairedSensor}
              />
            ) : null}
            {groupingDevices.smartPowerOutlet?._id ? (
              <InstallableDevice
                key={groupingDevices.smartPowerOutlet._id}
                device={groupingDevices.smartPowerOutlet}
              />
            ) : null}
            {groupingDevices.relayDevices.map(
              (device) =>
                device?._id && (
                  <InstallableDevice key={device._id} device={device} />
                ),
            )}
            {groupingDevices.otherThermostats.map(
              (device) =>
                device?._id && (
                  <InstallableDevice key={device._id} device={device} />
                ),
            )}
            {groupingDevices.otherMultiSensors.map(
              (device) =>
                device?._id && (
                  <InstallableDevice key={device._id} device={device} />
                ),
            )}
            {groupingDevices.conductorGenericSensors.map(
              (device) =>
                device?._id && (
                  <InstallableDevice key={device._id} device={device} />
                ),
            )}

            {groupingDevices.windowSensors.map(
              (device) =>
                device._id && (
                  <InstallableDevice key={device._id} device={device} />
                ),
            )}
            {groupingDevices.doorSensors.map(
              (device) =>
                device._id && (
                  <InstallableDevice key={device._id} device={device} />
                ),
            )}
            {groupingDevices.occupancySensors.map(
              (device) =>
                device?._id && (
                  <InstallableDevice key={device._id} device={device} />
                ),
            )}
            {groupingDevices.leakDetectors.map(
              (device) =>
                device?._id && (
                  <InstallableDevice key={device._id} device={device} />
                ),
            )}
          </Collapse>
        </List>
      ) : (
        <div
          className={`device-grouping-detail-${isSmall ? 'small' : 'large'}`}
          style={{
            width: isSmall ? '100%' : '400px',
            display: isSmall ? 'block' : 'flex',
            padding: isSmall ? '0px 2px 0px 3px' : '4px',
          }}
        >
          <BorderedSection
            containerStyle={{
              marginTop:
                groupingDevices.thermostat?._id ||
                groupingDevices.modbusAirHandler?._id
                  ? '0px'
                  : '14px',
            }}
            title={
              groupingDevices.thermostat?._id ||
              groupingDevices.modbusAirHandler?._id
                ? ''
                : grouping.name
                  ? grouping.name.split('_').join(' ')
                  : 'Unnamed Zone'
            }
            style={{
              width: '100%',
              maxWidth: isSmall ? 'unset' : '400px',
              margin: isSmall ? '0px 0px 12px 0px' : '0px',
            }}
            ccStyle={{
              padding: '3px',
              overflow: 'auto',
              marginTop: groupingDevices.thermostat?._id ? '0px' : '-6px',
            }}
          >
            {groupingDevices.thermostat?._id ? (
              <div
                style={{
                  width: '100%',
                  marginTop: '0px',
                  marginBottom: '0px',
                  paddingTop: '0px',
                  paddingBottom: '0px',
                  overflow: 'visible',
                }}
              >
                <ThermostatComponent
                  id={groupingDevices.thermostat._id}
                  property={property}
                  unit={unit}
                  preferredUnits={preferredUnits}
                  pairedSensor={groupingDevices.pairedSensor}
                  smartPowerOutlet={groupingDevices.smartPowerOutlet}
                />
              </div>
            ) : null}
            {groupingDevices.pairedSensor?._id ? (
              <div
                style={{
                  width: '100%',
                  marginTop: '0px',
                  marginBottom: '0px',
                  paddingTop: '0px',
                  paddingBottom: '0px',
                }}
              >
                <MultiSensorComponent
                  deviceId={groupingDevices.pairedSensor._id}
                  preferredUnits={preferredUnits}
                  pairedThermostat={groupingDevices.thermostat}
                />
              </div>
            ) : null}
            {groupingDevices.modbusAirHandler?._id ? (
              <div
                style={{
                  width: '100%',
                  marginTop: '0px',
                  marginBottom: '0px',
                  paddingTop: '0px',
                  paddingBottom: '0px',
                }}
              >
                <ModbusAirHandlerComponent
                  id={groupingDevices.modbusAirHandler._id}
                  preferredUnits={preferredUnits}
                  preferredAirFlowUnits={preferredAirFlowUnits}
                  pairedThermostat={groupingDevices.thermostat}
                />
              </div>
            ) : null}

            {groupingDevices.smartPowerOutlet?._id ? (
              <div
                style={{
                  width: '100%',
                  marginTop: '0px',
                  marginBottom: '0px',
                  paddingTop: '0px',
                  paddingBottom: '0px',
                }}
              >
                <SmartPowerOutletComponent
                  deviceId={groupingDevices.smartPowerOutlet._id}
                  pairedThermostat={groupingDevices.thermostat}
                />
              </div>
            ) : null}

            {groupingDevices.relayDevices.map(
              (device) =>
                device?._id && (
                  <div
                    key={device._id}
                    style={{
                      width: '100%',
                      marginTop: '0px',
                      marginBottom: '0px',
                      paddingTop: '0px',
                      paddingBottom: '0px',
                    }}
                  >
                    <RelayDeviceComponent deviceId={device._id} />
                  </div>
                ),
            )}

            {groupingDevices.otherThermostats.map(
              (device) =>
                device?._id && (
                  <div key={device._id} style={{ width: '100%' }}>
                    <ThermostatComponent
                      id={device._id}
                      property={property}
                      unit={unit}
                      preferredUnits={preferredUnits}
                    />
                  </div>
                ),
            )}
            {groupingDevices.otherMultiSensors.map(
              (device) =>
                device?._id && (
                  <div key={device._id} style={{ width: '100%' }}>
                    <MultiSensorComponent
                      deviceId={device._id}
                      preferredUnits={preferredUnits}
                    />
                  </div>
                ),
            )}
            {groupingDevices.conductorGenericSensors.map(
              (device) =>
                device?._id && (
                  <div key={device._id} style={{ width: '100%' }}>
                    <ConductorGenericSensorComponent deviceId={device._id} />
                  </div>
                ),
            )}

            {groupingDevices.windowSensors.map(
              (device) =>
                device._id && (
                  <div key={device._id} style={{ width: '100%' }}>
                    <WindowSensorComponent deviceId={device._id} />
                  </div>
                ),
            )}
            {groupingDevices.doorSensors.map(
              (device) =>
                device._id && (
                  <div key={device._id} style={{ width: '100%' }}>
                    <DoorSensorComponent deviceId={device._id} />
                  </div>
                ),
            )}
            {groupingDevices.occupancySensors.map(
              (device) =>
                device?._id && (
                  <div key={device._id} style={{ width: '100%' }}>
                    <OccupancySensorComponent deviceId={device._id} />
                  </div>
                ),
            )}
            {groupingDevices.leakDetectors.map(
              (device) =>
                device?._id && (
                  <div key={device._id} style={{ width: '100%' }}>
                    <LeakDetectorSensorComponent deviceId={device._id} />
                  </div>
                ),
            )}
          </BorderedSection>
        </div>
      );
    } else if (grouping.__typename === 'Panel') {
      if (installerView) {
        /* installer view does not apply to panel type groupings */
        return null;
      }

      const panel = grouping as Partial<Panel>;
      // const panelType = panel.type;
      const firstDevice = (panel.devices ?? [])[0] ?? {};

      switch (panel.type) {
        case EnumPanelType.Device:
          return panel.role === 'PowerMonitor' ? (
            (groupingDevices.powerMonitors ?? [])[0]?._id ? (
              <div
                className={`device-grouping-detail-${
                  isSmall ? 'small' : 'large'
                }`}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  overflow: 'auto',
                  margin: isSmall ? '6px 0px 6px 0px' : '5px',
                  padding: '0px',
                  borderRadius: '4px',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    marginTop: '0px',
                    marginBottom: '0px',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    overflow: 'visible',
                  }}
                >
                  <PowerMonitorComponent
                    deviceId={groupingDevices.powerMonitors[0]._id ?? 'nothing'}
                  />
                </div>
              </div>
            ) : (
              <div
                className={`device-grouping-detail-${
                  isSmall ? 'small' : 'large'
                }`}
              >
                No Power Monitors Installed
              </div>
            )
          ) : panel.role === 'Boiler' ? (
            (groupingDevices.boilers ?? [])[0]?._id ? (
              <div
                className={`device-grouping-detail-${
                  isSmall ? 'small' : 'large'
                }`}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  margin: isSmall ? '0px 0px 6px 0px' : '5px',
                  padding: '0px',
                  borderRadius: '4px',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    marginTop: '0px',
                    marginBottom: '0px',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }}
                >
                  <BoilerComponent
                    deviceId={groupingDevices.boilers[0]._id ?? 'nothing'}
                    preferredUnits={preferredUnits}
                  />
                </div>
              </div>
            ) : (
              <div
                className={`device-grouping-detail-${
                  isSmall ? 'small' : 'large'
                }`}
              >
                No Boilers Installed
              </div>
            )
          ) : panel.role === 'Chiller' ? (
            (groupingDevices.chillers ?? [])[0]?._id ? (
              <div
                className={`device-grouping-detail-${
                  isSmall ? 'small' : 'large'
                }`}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  margin: isSmall ? '0px 0px 6px 0px' : '5px',
                  padding: '0px',
                  borderRadius: '4px',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    marginTop: '0px',
                    marginBottom: '0px',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }}
                >
                  <ChillerComponent
                    deviceId={groupingDevices.chillers[0]._id ?? 'nothing'}
                    preferredUnits={preferredUnits}
                  />
                </div>
              </div>
            ) : (
              <div
                className={`device-grouping-detail-${
                  isSmall ? 'small' : 'large'
                }`}
              >
                No Chillers Installed
              </div>
            )
          ) : panel.type === 'Device' ? (
            <div
              className={`device-grouping-detail-${
                isSmall ? 'small' : 'large'
              }`}
              style={{
                width: '100%',
                maxWidth: '400px',
                margin: isSmall ? '0px 0px 6px 0px' : '5px',
                padding: '0px',
                borderRadius: '4px',
              }}
            >
              <div
                style={{
                  width: '100%',
                  marginTop: '0px',
                  marginBottom: '0px',
                  paddingTop: '0px',
                  paddingBottom: '0px',
                  paddingLeft: isSmall ? '1px' : '0px',
                }}
              >
                {firstDevice.type === EnumDKeyDeviceType.Thermistor ? (
                  <ThermistorComponent
                    deviceId={(panel.devices ?? [])[0]._id ?? 'nothing'}
                    label={panel.name ?? panel.role ?? 'Unnamed Thermistor'}
                    embedded={false}
                    preferredUnits={preferredUnits}
                  />
                ) : firstDevice.type === EnumDKeyDeviceType.PressureSensor ? (
                  <PressureSensorComponent
                    deviceId={(panel.devices ?? [])[0]._id ?? 'nothing'}
                    label={
                      panel.name ?? panel.role ?? 'Unnamed Pressure Sensor'
                    }
                  />
                ) : firstDevice.type === EnumDKeyDeviceType.OpenWeather ? (
                  <OpenWeatherComponent
                    deviceId={(panel.devices ?? [])[0]._id ?? 'nothing'}
                    label={
                      panel.name ??
                      panel.role ??
                      'Unnamed Outdoor Temperature Sensor'
                    }
                    embedded={false}
                  />
                ) : firstDevice.type === EnumDKeyDeviceType.Actuator ? (
                  <ActuatorComponent
                    deviceId={(panel.devices ?? [])[0]._id ?? 'nothing'}
                  />
                ) : firstDevice.type === EnumDKeyDeviceType.PowerMonitor ? (
                  <PowerMonitorComponent
                    deviceId={(panel.devices ?? [])[0]._id ?? 'nothing'}
                  />
                ) : firstDevice.type === EnumDKeyDeviceType.Chiller ? (
                  <ChillerComponent
                    deviceId={(panel.devices ?? [])[0]._id ?? 'nothing'}
                    preferredUnits={preferredUnits}
                  />
                ) : firstDevice.type === EnumDKeyDeviceType.Controller ? (
                  <ControllerComponent
                    deviceId={(panel.devices ?? [])[0]._id ?? 'nothing'}
                  />
                ) : firstDevice.type ? (
                  <div>
                    <div>{`Unknown Device Type: ${
                      ((panel.devices ?? [])[0] ?? {}).type
                    }`}</div>
                    <pre>{JSON.stringify(panel, null, 2)}</pre>
                  </div>
                ) : (
                  <div>No Devices Found</div>
                )}
              </div>
            </div>
          ) : (
            <div
              className={`device-grouping-detail-${
                isSmall ? 'small' : 'large'
              }`}
            >
              <pre>{JSON.stringify(panel, null, 2)}</pre>
            </div>
          );
        case EnumPanelType.Loop:
          return (
            <div
              className={`device-grouping-detail-${
                isSmall ? 'small' : 'large'
              }`}
              style={{
                width: '100%',
                maxWidth: '400px',
                overflow: 'auto',
                margin: isSmall ? '0px 0px 6px 0px' : '5px',
                padding: '0px',
                borderRadius: '4px',
              }}
            >
              <LoopComponent
                groupingDevices={groupingDevices}
                panelId={panel?._id ?? ''}
                preferredUnits={preferredUnits}
              />
            </div>
          );
        case EnumPanelType.Gpio:
          return (
            <div
              className={`device-grouping-detail-${
                isSmall ? 'small' : 'large'
              }`}
              style={{
                width: '100%',
                maxWidth: '400px',
                overflow: 'auto',
                margin: isSmall ? '0px 0px 6px 0px' : '5px',
                padding: '0px',
                borderRadius: '4px',
              }}
            >
              <GpioPanelComponent
                panelId={panel._id ?? ''}
                role={panel.role || 'GPIO'}
                gpioDevices={panel.devices as Partial<Gpio>[]}
              />
            </div>
          );
        default:
          return (
            <div
              className={`device-grouping-detail-${
                isSmall ? 'small' : 'large'
              }`}
            >
              Unknown Panel Type
            </div>
          );
      }
    } else if (grouping.__typename === 'VirtualPowerPlant') {
      const participants = {
        energyConsumerTenants: grouping.consumerTenants ?? [],
        energyConsumerBuildings: grouping.consumerBuildings ?? [],
        energyConsumerUtilities: grouping.consumerUtilities ?? [],
        energyStorageKineticSystems: grouping.storageKineticSystems ?? [],
        energyStorageBatterySystems: grouping.storageBatterySystems ?? [],
        energySupplierSolarSystems: grouping.supplierSolarSystems ?? [],
        energySupplierWindSystems: grouping.supplierWindSystems ?? [],
        energySupplierUtilities: grouping.supplierUtilities ?? [],
      };

      const hasParticipants = Object.values(participants).some(
        (participantArray) => participantArray.length > 0,
      );

      return hasParticipants && groupingDevices ? (
        <div
          className={`device-grouping-detail-${isSmall ? 'small' : 'large'}`}
          style={{
            width: isSmall ? '100%' : '400px',
            display: isSmall ? 'block' : 'flex',
            padding: isSmall ? '0px 2px 0px 3px' : '4px',
          }}
        >
          <BorderedSection
            title={grouping.name ? grouping.name : 'Unnamed VPP'}
            style={{
              width: '100%',
              maxWidth: isSmall ? 'unset' : '400px',
              margin: isSmall ? '0px 0px 12px 0px' : '0px',
            }}
            ccStyle={{
              padding: '3px',
              overflow: 'auto',
              marginTop: groupingDevices ? '0px' : '-10px',
            }}
          >
            {grouping._id ? (
              <VirtualPowerPlantComponent id={grouping._id} />
            ) : null}
            {groupingDevices.energySupplierSolarSystems.map(
              (participant) =>
                participant?._id && (
                  <div key={participant._id} style={{ width: '100%' }}>
                    <EnergySupplierSolarSystemComponent
                      participantId={participant._id}
                    />
                  </div>
                ),
            )}
            {groupingDevices.energySupplierWindSystems.map(
              (participant) =>
                participant?._id && (
                  <div key={participant._id} style={{ width: '100%' }}>
                    <EnergySupplierWindSystemComponent
                      participantId={participant._id}
                    />
                  </div>
                ),
            )}
            {groupingDevices.energySupplierUtilities.map(
              (participant) =>
                participant?._id && (
                  <div key={participant._id} style={{ width: '100%' }}>
                    <EnergySupplierUtilityComponent
                      participantId={participant._id}
                    />
                  </div>
                ),
            )}

            {groupingDevices.energyStorageBatterySystems.map(
              (participant) =>
                participant?._id && (
                  <div key={participant._id} style={{ width: '100%' }}>
                    <EnergyStorageBatterySystemComponent
                      participantId={participant._id}
                    />
                  </div>
                ),
            )}
            {groupingDevices.energyStorageKineticSystems.map(
              (participant) =>
                participant?._id && (
                  <div key={participant._id} style={{ width: '100%' }}>
                    <EnergyStorageKineticSystemComponent id={participant._id} />
                  </div>
                ),
            )}

            {groupingDevices.energyConsumerBuildings.map(
              (participant) =>
                participant?._id && (
                  <div key={participant._id} style={{ width: '100%' }}>
                    <EnergyConsumerBuildingComponent id={participant._id} />
                  </div>
                ),
            )}
            {groupingDevices.energyConsumerTenants.map(
              (participant) =>
                participant?._id && (
                  <div key={participant._id} style={{ width: '100%' }}>
                    <EnergyConsumerTenantComponent id={participant._id} />
                  </div>
                ),
            )}
            {groupingDevices.energyConsumerUtilities.map(
              (participant) =>
                participant?._id && (
                  <div key={participant._id} style={{ width: '100%' }}>
                    <EnergyConsumerUtilityComponent id={participant._id} />
                  </div>
                ),
            )}
          </BorderedSection>
        </div>
      ) : (
        <div
          className={`device-grouping-detail-${isSmall ? 'small' : 'large'}`}
        >
          No Virtual Power Plants Installed
        </div>
      );
    } else {
      /* In this case the grouping is neither a zone nor a panel nor a VPP.
       * Very strange indeed, best to do nothing.
       */
      console.warn(
        '[device-grouping-detail] device grouping is neither a zone or a panel',
      );
      return null;
    }
  } else {
    return (
      <div className={`device-grouping-detail-${isSmall ? 'small' : 'large'}`}>
        No Devices
      </div>
    );
  }
}
